import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Segmented } from 'antd';
import { SegmentedLabeledOption, SegmentedValue } from 'antd/es/segmented';
import { motion } from 'framer-motion';
import { match } from 'ts-pattern';
import { ICollapseItems } from '../collapse/Collapse';
import { Container, Content, RedDot, SegmentedWrapper } from './Navigation.style';
interface Props {
  elOffset: {
    ingredients: number;
    generalInformation: number;
    nutritionalInformation: number;
    howToCook: number;
    storageMethod: number;
    distributionChannels: number;
  };
  openSection: ICollapseItems['key'][];
  setOpenSection: (val: ICollapseItems['key'][]) => void;
  updateTrigger: () => void;
  headerVisibility: boolean;
  setHeaderVisibility: (val: boolean) => void;
  menuList: Array<{
    label: string;
    value: ICollapseItems['key'];
    offset: number;
  }>;
}
const ProductDetailNavigation: FC<Props> = ({
  elOffset,
  openSection,
  setOpenSection,
  updateTrigger,
  headerVisibility,
  setHeaderVisibility,
  menuList
}: Props) => {
  const [activeNav, setActiveNav] = useState<ICollapseItems['key']>('general-information');
  const [offset, setOffset] = useState(0);
  const [scrollDirection, setScrollDirection] = useState(0);
  const headerNavigatorRef = useRef<HTMLDivElement>();
  const handleNavigation = useCallback(e => {
    const window = e.currentTarget;
    if (scrollDirection > window.scrollY) {
      if (headerVisibility) {
        headerNavigatorRef.current.style.top = '0px';
      } else {
        headerNavigatorRef.current.style.top = '72px';
      }
    } else if (scrollDirection < window.scrollY) {
      headerNavigatorRef.current.style.top = '0px';
    }
    setScrollDirection(window.scrollY);
  }, [scrollDirection]);
  useEffect(() => {
    setScrollDirection(window.scrollY);
    window.addEventListener('scroll', handleNavigation);
    return () => {
      window.removeEventListener('scroll', handleNavigation);
    };
  }, [handleNavigation]);
  useEffect(() => {
    const onScroll = () => setOffset(window.scrollY + 1);

    // clean up code
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    window.addEventListener('scrollend', () => setHeaderVisibility(false));
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
  useEffect(() => {
    setActiveNav(handleMenuActive());
  }, [offset, elOffset]);
  const handleMenuActive = (): ICollapseItems['key'] => {
    if (menuList?.length) {
      if (offset == 0) {
        return menuList[0].value;
      }
      for (let i = menuList.length - 1; i >= 0; i--) {
        if (offset >= menuList[i].offset) {
          return menuList[i].value;
        }
      }
    }
  };
  const isMenuActive = (val: ICollapseItems['key']): boolean => {
    return val === activeNav;
  };
  const optionsList: (SegmentedValue | SegmentedLabeledOption)[] = menuList.map(({
    label,
    value
  }) => {
    return {
      label: <Container>
                        <motion.div animate={{
          opacity: isMenuActive(value) ? 1 : 0
        }} transition={{
          duration: 0.5,
          type: 'tween'
        }}>
                            <RedDot />
                        </motion.div>
                        <Content className={isMenuActive(value) ? 'active' : ''}>
                            {label}
                        </Content>
                    </Container>,
      value
    };
  });
  const onSelectNavMenu = async (val: ICollapseItems['key']) => {
    const openSectionRemoveDuplicateVal = [...new Set([...openSection, val])];
    await setOpenSection(openSectionRemoveDuplicateVal);
    setTimeout(updateTrigger, 800);
    window.scroll({
      top: getMenuHeightPosition(val),
      left: 0,
      behavior: 'smooth'
    });
  };
  const getMenuHeightPosition = (val: ICollapseItems['key']): number => {
    return match(val).with('ingredients', () => elOffset.ingredients).with('general-information', () => elOffset.generalInformation).with('nutritional-information', () => elOffset.nutritionalInformation).with('how-to-cook', () => elOffset.howToCook).with('storage-method', () => elOffset.storageMethod).with('distribution-channels', () => elOffset.distributionChannels).exhaustive();
  };
  return <SegmentedWrapper ref={headerNavigatorRef}>
            <Segmented block value={activeNav} onClick={() => setHeaderVisibility(true)} onChange={e => onSelectNavMenu(e as ICollapseItems['key'])} options={optionsList} />
        </SegmentedWrapper>;
};
export default ProductDetailNavigation;